import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { reduxForm } from "redux-form";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import { filteredEditVersionValues } from "selectors/VersionDetails";
import { setVariableSearchTerm } from "reducers/VersionDetails";

import { Button } from "rhombus-react";
import TextEditor from "./TextEditor";
import AssetEditor from "./AssetEditor";
import EmptySection from "widgets/EmptySection";
import { isAfterEffectsCreative, isImposiumCreative, isSocialCreative } from "utils/common";
const editorsByType = {
	Text: TextEditor,
	Image: AssetEditor,
	Video: AssetEditor,
	Audio: AssetEditor,
	Richload: AssetEditor,
};

export function shouldUseUnencodedVideos(creative = {}) {
	let isAdStudioCreative = /adstudio/gi.test(creative.origin);
	let isDynamicPrerollCreative = /dynamic_preroll/gi.test(creative.adType);
	const isAfterEffects = isAfterEffectsCreative(creative);
	const isSocial = isSocialCreative(creative);
	const isImposium = isImposiumCreative(creative);

	return [
		isAdStudioCreative && isDynamicPrerollCreative,
		isAfterEffects,
		isSocial,
		isImposium
	].some(cond => cond);
}

export function getAccept(type, creative = {}, variableName = ""){
	/*
		We usually don't need this because we can just rely on the asset type filtering,
		but if you need to add any special restrictions on file types, it can be done here.

		If nothing is returned here, accept will effectively default to "*"

		The returned value is expected to be a string with a comma separated list of acceptable file extensions,
		with no spaces, in lower case. e.g. ".mp4,.mov"
			- Non-specific types (e.g. "video/*"), which are appropriate for file inputs, are not appropriate
			here.

		This value will affect selectable rows with the SelectAssets modal, selectable files if
		the Upload button is used in the SelectAssets modal, and available options in the
		search/select dropdown in AssetEditor rows.
	*/
	const isAfterEffects = isAfterEffectsCreative(creative);
	if(isAfterEffects && variableName){
		const periodIndex = variableName.lastIndexOf('.');
		if(periodIndex > 0){
			return variableName.slice(periodIndex).toLowerCase();
		}
	}
}

function NoSearchResults() {
	const dispatch = useDispatch();
	let emptyProps = {
		className: "empty-variables no-search-results",
		header: "No search results to display",
		message: "Try adjusting your search to find what you are looking for",
		src: "/images/noSearch.svg",
		buttons: [
			<Button key="clear" onClick={() => dispatch(setVariableSearchTerm(""))}>Clear Search</Button>
		],
	};
	return <EmptySection {...emptyProps} />;
}

export function EditVersion(props) {
	const {
		searchAssets,
		handleSubmit,
		onSave,
		versionInfo,
		libraryId,
		saveVersion,
		saveAsVersion,
	} = props;

	const filteredValues = useSelector(state => filteredEditVersionValues(state, EDIT_VERSION_BLADE_FORM));
	const searchTerm = useSelector(state => state.versionDetails.searchTerm);
	const creativeDetails = useSelector(state => state.creativeDetails);
	const isSocial = isSocialCreative(creativeDetails);

	let videoEncodingFilter = 'true'; //string because this is a url query string param
	if (shouldUseUnencodedVideos(creativeDetails)) {
		videoEncodingFilter = 'false';
	}

	const VariableRows = useMemo(() => {
		if (!filteredValues) {
			return null;
		}

		return Object.values(filteredValues).map((value) => {
			let { name, type } = value;
			let Editor = editorsByType[type];
			const accept = getAccept(type, creativeDetails, name);
			const showEditButton = isImposiumCreative(creativeDetails) && (type === "Image" || type === "Video");

			return (
				<Editor
					key={name}
					value={value}
					form={EDIT_VERSION_BLADE_FORM}
					search={searchAssets}
					libraryId={libraryId}
					versionInfo={versionInfo}
					videoEncodingFilter={videoEncodingFilter}
					accept={accept}
					saveVersion={saveVersion}
					saveAsVersion={saveAsVersion}
					disabledTextAssets={isSocial}
					showEditButton={showEditButton}
				/>
			);
		});
	}, [creativeDetails, filteredValues, isSocial]);

	if (searchTerm && (Object.keys(filteredValues)?.length === 0)) {
		return <NoSearchResults />;
	}

	return (
		<div id="edit-version-table" className="edit-version-table" >
			<form className="form" id={EDIT_VERSION_BLADE_FORM} onSubmit={handleSubmit(onSave)}>
				<table>
					<tbody>
						{VariableRows}
					</tbody>
				</table>
			</form>
		</div>
	);
}

export default reduxForm({
	form: EDIT_VERSION_BLADE_FORM,
	destroyOnUnmount: false,
})(EditVersion);

EditVersion.displayName = "EditVersion";
